/**
 * Gate Evaluation — Verdict logic for a single question's issues.
 *
 * Split from issueContract.ts (C1, Sprint 5). This is the SSOT for:
 *  - GateThresholds (configurable scoring thresholds)
 *  - DEFAULT_GATE_THRESHOLDS (fallback values — runtime values come from policyEngine)
 *  - evaluateGate() (the gate-logic itself)
 *
 * Gate rules (from architecture doc):
 *  - productionGate: no blockers + score ≥ productionMinScore
 *  - assessmentGate: no blockers + no high-severity coach + score ≥ assessmentMinScore
 *  - reviewGate: coach severity ≥ reviewCoachSeverityThreshold → REVIEW_NEEDED
 */

import { computeIssuePenalty } from '../issues/builders';
import { getScoringWeights, isVisualPolicyDegraded } from '../policyEngine';
import type { GateResult, GateVerdict, ValidationIssue } from '../issues/types';
import { CRITICAL_BLOCKER_SEVERITY_THRESHOLD, REVIEW_COACH_SEVERITY_THRESHOLD } from '../../utils/severity';

// ─── Gate Thresholds ──────────────────────────────────────────────────────────

/** Gate thresholds (configurable via policy) */
export interface GateThresholds {
  /** No blockers + score ≥ this → production eligible */
  productionMinScore: number;

  /** No blockers + no high-severity coach + score ≥ this → assessment eligible */
  assessmentMinScore: number;

  /** Coach severity ≥ this → REVIEW_NEEDED */
  reviewCoachSeverityThreshold: number;

  /** Minimum questions per family for assessment pool */
  assessmentMinQuestionsPerFamily: number;
}

/** Default gate thresholds.
 * NOTE: These are fallback values only. The active thresholds come from
 * defaultPolicy.ts GATES via policyEngine.getGateThresholds().
 * Kept aligned with defaultPolicy.ts for consistency (SCAN-042 fix).
 */
export const DEFAULT_GATE_THRESHOLDS: GateThresholds = {
  productionMinScore: 70,
  assessmentMinScore: 82,
  // SSOT: governance/utils/severity.ts (detangle §10.5 consolidation).
  reviewCoachSeverityThreshold: REVIEW_COACH_SEVERITY_THRESHOLD,
  assessmentMinQuestionsPerFamily: 3
};

// ─── Gate Evaluation ──────────────────────────────────────────────────────────

/**
 * Evaluate gate logic for a set of issues.
 */
export function evaluateGate(issues: ValidationIssue[], thresholds: GateThresholds = DEFAULT_GATE_THRESHOLDS): GateResult {
  const blockers = issues.filter(i => i.kind === 'blocker');
  const coaches = issues.filter(i => i.kind === 'coach');
  const infos = issues.filter(i => i.kind === 'info');

  // Score: start at 100, subtract penalties.
  // Phase 4: pass live policy scoring weights so admin-tuned overrides take
  // effect. With no override this preserves the historical 30/÷4/2 formula.
  const penalty = computeIssuePenalty(issues, getScoringWeights());
  const score = Math.max(0, 100 - penalty);

  // Gate evaluation
  const hasBlockers = blockers.length > 0;
  // NOTE: This is a SINGLE-coach threshold check, not a sum/stacking.
  // One coach with severity ≥ reviewCoachSeverityThreshold (default 45)
  // triggers REVIEW_NEEDED. Multiple lower-severity coaches do NOT stack.
  // Verified Ruis-audit v1.5 §25.5 — "severity-stacking" term was misleading.
  const hasHighSeverityCoach = coaches.some(c => c.severity >= thresholds.reviewCoachSeverityThreshold);
  const productionEligible = !hasBlockers && score >= thresholds.productionMinScore;
  const assessmentEligible = !hasBlockers && !hasHighSeverityCoach && score >= thresholds.assessmentMinScore;
  const reviewNeeded = hasHighSeverityCoach;

  // Determine verdict
  // NOTE (Phase 1): The verdict logic is UNCHANGED. We only enrich the
  // reason-string with a '[KRITIEK]' prefix when any blocker has severity ≥
  // CRITICAL_BLOCKER_SEVERITY_THRESHOLD (90) so the admin UI can highlight
  // student-facing risks (answer-leak, indicator parity) without affecting
  // gate behavior. Zero verdict-shifts on 263 families. The threshold is
  // imported from utils/severity to keep gate verdict logic and display
  // severity in sync (single source of truth).
  const hasCriticalBlocker = blockers.some(b => b.severity >= CRITICAL_BLOCKER_SEVERITY_THRESHOLD);
  let verdict: GateVerdict;
  let reason: string;
  if (hasBlockers) {
    verdict = 'FAIL';
    const prefix = hasCriticalBlocker ? '[KRITIEK] ' : '';
    reason = `${prefix}${blockers.length} blocker(s) found: ${blockers.map(b => b.code).join(', ')}`;
  } else if (reviewNeeded) {
    verdict = 'REVIEW_NEEDED';
    reason = `High-severity coach issue(s) require review (score: ${score})`;
  } else if (score < thresholds.productionMinScore) {
    verdict = 'FAIL';
    reason = `Score ${score} below production threshold (${thresholds.productionMinScore})`;
  } else {
    verdict = 'PASS';
    reason = `Score ${score}, no blockers, ${productionEligible ? 'production' : ''}${assessmentEligible ? '+assessment' : ''} eligible`;
  }

  // ── Visual-policy degraded gate-integratie (S-STAB-2, R8 gate-deel) ─────────
  //
  // Wanneer de visual policy permanent gefaald is, draaien visual-stage
  // answer-leakage checks op de default lege policy. Zonder deze check kon
  // een individuele vraag stil als PASS door de gate komen — alleen de
  // admin-banner waarschuwde ("een degraded run kan dus stil als groen
  // presenteren"). Zie docs/architecture/GOVERNANCE_PIPELINE.md (visual
  // policy degradation) en docs/audits/GOVERNANCE_DOMAIN_CONSOLIDATED_AUDIT_2026.md.
  //
  // Gedrag:
  //   - FAIL  → blijft FAIL (alleen [VISUAL-DEGRADED] prefix toegevoegd)
  //   - REVIEW_NEEDED → blijft REVIEW_NEEDED (prefix toegevoegd)
  //   - PASS  → wordt gedowngrade naar REVIEW_NEEDED (prefix toegevoegd)
  //
  // De flag is runtime state (gezet door policyEngine na 3 build failures);
  // wanneer false is gedrag identiek aan vóór deze fix (back-compat garantie).
  let effectiveReviewNeeded = reviewNeeded;
  if (isVisualPolicyDegraded()) {
    reason = `[VISUAL-DEGRADED] ${reason}`;
    if (verdict === 'PASS') {
      verdict = 'REVIEW_NEEDED';
      effectiveReviewNeeded = true;
    }
  }
  return {
    verdict,
    productionEligible,
    assessmentEligible,
    reviewNeeded: effectiveReviewNeeded,
    score,
    issues,
    counts: {
      blockers: blockers.length,
      coaches: coaches.length,
      infos: infos.length,
      total: issues.length
    },
    reason
  };
}